import React, { useEffect, useState } from "react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Paper,
	Snackbar,
	Typography,
	useTheme,
} from "@mui/material";
import {
	AddOutlined,
	ContentCopyOutlined,
	RefreshOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";
import InternoModalConfirmacaoSaida from "../../../components/modalSaindoDaPagina";
import api from "../../../services/api";
import ModalLicenca from "./ModalLicenca";
import useStyles from "./styles";

const utc = require("dayjs/plugin/utc");
const timezone = require("dayjs/plugin/timezone");

dayjs.extend(utc);
dayjs.extend(timezone);

const timeZone = "America/Sao_Paulo";

const ROTULOS_STATUS = {
	ATIVA: "ATIVA",
	EXPIRANDO: "EXPIRANDO",
	EXPIRADA: "EXPIRADA",
	REVOGADA: "REVOGADA",
	NAO_PROVISIONADA: "PRIMEIRA CONEXÃO",
	NUNCA_EMITIDA: "SEM EMISSÃO",
	SEM_LICENCA: "SEM LICENÇA",
};

const MS_DIA = 24 * 60 * 60 * 1000;

const formatarData = (valor) =>
	valor ? dayjs(valor).tz(timeZone).format("DD/MM/YYYY HH:mm:ss") : "—";

const diasRestantes = (licenca) => {
	if (!licenca?.expiraEm) return null;

	const restanteMs = new Date(licenca.expiraEm).getTime() - Date.now();
	return restanteMs <= 0 ? 0 : Math.ceil(restanteMs / MS_DIA);
};

const nomeDoCliente = (licenca) =>
	licenca?.cliente?.nomeFantasia ||
	licenca?.cliente?.razaoSocial ||
	licenca?.clienteId;

export default function Licencas() {
	const theme = useTheme();
	const styles = useStyles(theme);

	const [licencas, setLicencas] = useState([]);
	const [clientes, setClientes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [snackbar, setSnackbar] = useState(null);
	const [openModal, setOpenModal] = useState(false);
	const [licencaEmEdicao, setLicencaEmEdicao] = useState(null);
	const [linhaSelecionada, setLinhaSelecionada] = useState(null);
	const [confirmarRevogacao, setConfirmarRevogacao] = useState(false);
	const [acaoEmCurso, setAcaoEmCurso] = useState(null);

	useEffect(() => {
		buscarDados();
	}, []);

	async function buscarDados() {
		setLoading(true);
		try {
			const [resLicencas, resClientes] = await Promise.all([
				api.get("/interno/licencas"),
				api.get("/getClients"),
			]);
			setLicencas(resLicencas?.data || []);
			setClientes(resClientes?.data || []);
		} catch (error) {
			setSnackbar({
				severity: "error",
				children: "Erro ao buscar licenças",
			});
		} finally {
			setLoading(false);
		}
	}

	const abrirCriacao = () => {
		setLicencaEmEdicao(null);
		setOpenModal(true);
	};

	const abrirEdicao = (licenca) => {
		setLicencaEmEdicao(licenca);
		setOpenModal(true);
	};

	const copiarClienteId = (licenca) => {
		navigator.clipboard?.writeText(licenca?.clienteId || "");
		setSnackbar({
			children: "LICENCA_CLIENTE_ID copiado",
			severity: "success",
		});
	};

	const pedirRevogacao = (licenca) => {
		setLinhaSelecionada(licenca);
		setConfirmarRevogacao(true);
	};

	async function alterarStatus(licenca, status, motivo) {
		setAcaoEmCurso(licenca?.clienteId);
		try {
			const response = await api.post("/interno/licencas/status", {
				clienteId: licenca?.clienteId,
				status,
				motivo,
			});

			const notificacao = response?.data?.notificacao;
			setSnackbar({
				severity: notificacao?.alcancado ? "success" : "warning",
				children: notificacao?.alcancado
					? "Licença atualizada e instalação notificada."
					: `Licença atualizada. ${notificacao?.motivo || ""}`,
			});

			await buscarDados();
		} catch (error) {
			setSnackbar({
				severity: "error",
				children: error?.response?.data?.error || "Erro ao alterar status",
			});
		} finally {
			setAcaoEmCurso(null);
			setConfirmarRevogacao(false);
		}
	}

	async function forcarRenovacao(licenca) {
		setAcaoEmCurso(licenca?.clienteId);
		try {
			const response = await api.post("/interno/licencas/forcar-renovacao", {
				clienteId: licenca?.clienteId,
			});

			setSnackbar({
				severity: response?.data?.alcancado ? "success" : "warning",
				children: response?.data?.alcancado
					? "Instalação notificada."
					: response?.data?.motivo || "Instalação não foi alcançada.",
			});

			await buscarDados();
		} catch (error) {
			setSnackbar({
				severity: "error",
				children:
					error?.response?.data?.error || "Erro ao notificar instalação",
			});
		} finally {
			setAcaoEmCurso(null);
		}
	}

	async function abrirProvisionamento(licenca, reset = false) {
		setAcaoEmCurso(licenca?.clienteId);
		try {
			await api.post("/interno/licencas/provisionamento", {
				clienteId: licenca?.clienteId,
				reset,
			});

			setSnackbar({
				severity: "success",
				children: reset
					? "Provisionamento reaberto. A instalação pode se registrar de novo."
					: "Provisionamento aberto. A instalação pega o segredo no próximo ciclo.",
			});

			await buscarDados();
		} catch (error) {
			setSnackbar({
				severity: "error",
				children:
					error?.response?.data?.error || "Erro ao abrir provisionamento",
			});
		} finally {
			setAcaoEmCurso(null);
		}
	}

	async function rotacionarSegredo(licenca) {
		setAcaoEmCurso(licenca?.clienteId);
		try {
			const response = await api.post("/interno/licencas/rotacionar", {
				clienteId: licenca?.clienteId,
			});

			const notificacao = response?.data?.notificacao;
			setSnackbar({
				severity: notificacao?.alcancado ? "success" : "warning",
				children: notificacao?.alcancado
					? "Segredo rotacionado e entregue à instalação."
					: "Segredo rotacionado. A entrega acontece no próximo ciclo da instalação.",
			});

			await buscarDados();
		} catch (error) {
			setSnackbar({
				severity: "error",
				children: error?.response?.data?.error || "Erro ao rotacionar segredo",
			});
		} finally {
			setAcaoEmCurso(null);
		}
	}

	const renderCard = (licenca) => {
		const status = licenca?.statusDerivado;
		const dias = diasRestantes(licenca);
		const mostraDias = dias !== null && status !== "REVOGADA";
		const ocupado = acaoEmCurso === licenca?.clienteId;
		const revogada = licenca?.status === "REVOGADA";
		const provisionada = !!licenca?.segredoEntregueEm;

		return (
			<Paper key={licenca.id} elevation={0} sx={styles.card}>
				<Box sx={styles.trilho(status)}>
					<Typography sx={styles.trilhoValor(status)}>
						{mostraDias ? dias : "—"}
					</Typography>
					{mostraDias && (
						<Typography sx={styles.trilhoUnidade}>
							{dias === 1 ? "DIA" : "DIAS"}
						</Typography>
					)}
					<Typography sx={styles.trilhoStatus(status)}>
						{ROTULOS_STATUS[status] || status}
					</Typography>
				</Box>

				<Box sx={styles.corpo}>
					<Box sx={styles.cabecalho}>
						<Typography sx={styles.nomeCliente}>
							{nomeDoCliente(licenca)}
						</Typography>
						<Button
							size="small"
							sx={styles.copiarId}
							startIcon={<ContentCopyOutlined sx={styles.copiarIdIcone} />}
							onClick={() => copiarClienteId(licenca)}
						>
							copiar ID
						</Button>
					</Box>

					<Box sx={styles.metadados}>
						<Box sx={styles.metadado}>
							<Typography sx={styles.rotulo}>ÚLTIMA EMISSÃO</Typography>
							<Typography sx={styles.valor(!!licenca?.emitidaEm)}>
								{formatarData(licenca?.emitidaEm)}
							</Typography>
						</Box>
						<Box sx={styles.metadado}>
							<Typography sx={styles.rotulo}>EXPIRA EM</Typography>
							<Typography sx={styles.valor(!!licenca?.expiraEm)}>
								{formatarData(licenca?.expiraEm)}
							</Typography>
						</Box>
						<Box sx={styles.metadado}>
							<Typography sx={styles.rotulo}>ÚLTIMO CONTATO</Typography>
							<Typography sx={styles.valor(!!licenca?.ultimoContatoEm)}>
								{formatarData(licenca?.ultimoContatoEm)}
							</Typography>
						</Box>
					</Box>

					<Box sx={styles.acoes}>
						{provisionada ? (
							<Button
								variant="contained"
								size="small"
								sx={styles.botao}
								disabled={ocupado || !licenca?.urlBase}
								onClick={() => forcarRenovacao(licenca)}
							>
								Forçar renovação
							</Button>
						) : (
							<Button
								variant="contained"
								size="small"
								sx={styles.botao}
								disabled={ocupado || licenca?.provisionamento}
								onClick={() => abrirProvisionamento(licenca)}
							>
								{licenca?.provisionamento
									? "Aguardando instalação"
									: "Primeira conexão"}
							</Button>
						)}
						<Button
							variant="outlined"
							size="small"
							sx={styles.botao}
							onClick={() => abrirEdicao(licenca)}
						>
							Editar
						</Button>
						{/* {provisionada && (
							<Button
								variant="outlined"
								size="small"
								sx={styles.botao}
								disabled={ocupado || licenca?.rotacaoPendente}
								onClick={() => rotacionarSegredo(licenca)}
							>
								{licenca?.rotacaoPendente
									? "Entrega pendente"
									: "Rotacionar segredo"}
							</Button>
						)} */}
						{/* <Button
              variant="outlined"
              size="small"
              sx={styles.botao}
              onClick={() => copiarClienteId(licenca)}
            >
              Copiar ID do cliente
            </Button> */}
						<Box sx={styles.espacador} />
						{revogada ? (
							<Button
								variant="outlined"
								size="small"
								sx={styles.botaoReativar}
								disabled={ocupado}
								onClick={() => alterarStatus(licenca, "ATIVA")}
							>
								Reativar
							</Button>
						) : (
							<Button
								variant="outlined"
								size="small"
								sx={styles.botaoRevogar}
								disabled={ocupado}
								onClick={() => pedirRevogacao(licenca)}
							>
								Revogar
							</Button>
						)}
					</Box>
				</Box>
			</Paper>
		);
	};

	const renderConteudo = () => {
		if (loading && !licencas.length) {
			return (
				<Box sx={styles.aviso}>
					<CircularProgress size={26} />
				</Box>
			);
		}

		if (!licencas.length) {
			return (
				<Box sx={styles.aviso}>
					<Typography sx={styles.avisoTexto}>
						Nenhuma licença cadastrada.
					</Typography>
				</Box>
			);
		}

		return licencas.map(renderCard);
	};

	return (
		<Box sx={styles.pageBox}>
			<Paper
				elevation={theme.palette.mode === "dark" ? 0 : 2}
				sx={styles.outerPaper}
			>
				<Box sx={styles.headerBox}>
					<Typography variant="h5" sx={styles.title}>
						Licenças
					</Typography>
					<Box sx={styles.actionsBox}>
						<Button
							variant="outlined"
							startIcon={<RefreshOutlined />}
							onClick={buscarDados}
							disabled={loading}
						>
							Atualizar
						</Button>
						<Button
							variant="contained"
							color="primary"
							startIcon={<AddOutlined />}
							onClick={abrirCriacao}
						>
							Nova Licença
						</Button>
					</Box>
				</Box>

				<Box sx={styles.lista}>{renderConteudo()}</Box>
			</Paper>

			<ModalLicenca
				open={openModal}
				setOpen={setOpenModal}
				licenca={licencaEmEdicao}
				clientes={clientes}
				licencas={licencas}
				setSnackbar={setSnackbar}
				onSalvo={buscarDados}
			/>

			<InternoModalConfirmacaoSaida
				openConfirmacao={confirmarRevogacao}
				setOpenConfirmacao={setConfirmarRevogacao}
				severity="warning"
				title="Revogar licença"
				headerText="Esta ação bloqueia o sistema do cliente."
				contentText="A instalação será notificada agora se estiver acessível. Caso contrário, o bloqueio entra em vigor no próximo ciclo de renovação."
				confirmText="Revogar"
				confirmLoad={acaoEmCurso === linhaSelecionada?.clienteId}
				execute={() => alterarStatus(linhaSelecionada, "REVOGADA")}
				cancel={() => setConfirmarRevogacao(false)}
			/>

			<Snackbar
				open={!!snackbar}
				autoHideDuration={5000}
				onClose={() => setSnackbar(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setSnackbar(null)}
					severity={snackbar?.severity}
					sx={{ width: "100%" }}
				>
					{snackbar?.children}
				</Alert>
			</Snackbar>
		</Box>
	);
}
